import React, { useEffect, useState } from 'react';
import { veiculoService } from '../services/veiculoService';
import { Veiculo, TipoVeiculo, VeiculoMarca, VeiculoModelo, VeiculoCor, VeiculoAno } from '../types/veiculo';
import { Plus, Search, Truck, Edit, Trash2, ChevronDown, ChevronUp, User, Building2, Wrench, ClipboardCheck, Fuel, Calendar, Clock, AlertTriangle, LayoutDashboard, ListFilter, Users, History, ShieldCheck, Merge, ShieldAlert, CheckCircle2, FileText, Eye } from 'lucide-react';
import { CrlvFieldInput } from '../components/crlv/CrlvFieldInput';
import { CrlvViewerModal } from '../components/crlv/CrlvViewerModal';
import { CrlvFile } from '../types/crlv';
import { Pagination } from '../components/common/Pagination';
import { VeiculoManutencaoModal } from '../components/veiculos/VeiculoManutencaoModal';
import { VeiculoChecklistModal } from '../components/veiculos/VeiculoChecklistModal';
import { VeiculoAbastecimentoModal } from '../components/veiculos/VeiculoAbastecimentoModal';
import { VeiculoHistoricoMotoristasModal } from '../components/veiculos/VeiculoHistoricoMotoristasModal';
import { VeiculoConsolidacaoModal } from '../components/veiculos/VeiculoConsolidacaoModal';
import { veiculoHistoricoMotoristaService } from '../services/veiculoHistoricoMotoristaService';
import { VeiculosDashboard } from '../components/veiculos/VeiculosDashboard';
import { VeiculoGestaoFrotaTable } from '../components/veiculos/VeiculoGestaoFrotaTable';
import { ProtectedField } from '../components/common/ProtectedField';

export function Veiculos() {
  const [activeTab, setActiveTab] = useState<'cadastro' | 'gestao' | 'dashboard'>('cadastro');
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Accordion expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pessoasMap, setPessoasMap] = useState<Record<number, string>>({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Column filter states
  const [filterTipo, setFilterTipo] = useState<number | ''>('');
  const [filterMarca, setFilterMarca] = useState<number | ''>('');
  const [filterCor, setFilterCor] = useState<number | ''>('');
  const [filterAno, setFilterAno] = useState<number | ''>('');
  const [filterMotorista, setFilterMotorista] = useState<number | ''>('');
  const [filterAgregado, setFilterAgregado] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<'todos' | 'ativos' | 'inativos' | 'em_manutencao' | 'disponivel' | 'em_uso'>('todos');

  // Feature Modal States
  const [manutencaoVeiculo, setManutencaoVeiculo] = useState<Veiculo | null>(null);
  const [checklistVeiculo, setChecklistVeiculo] = useState<Veiculo | null>(null);
  const [abastecimentoVeiculo, setAbastecimentoVeiculo] = useState<Veiculo | null>(null);
  const [historicoVeiculo, setHistoricoVeiculo] = useState<Veiculo | null>(null);
  const [isConsolidacaoOpen, setIsConsolidacaoOpen] = useState(false);
  const [confirmToggleVeiculo, setConfirmToggleVeiculo] = useState<Veiculo | null>(null);
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [selectedCrlvFile, setSelectedCrlvFile] = useState<CrlvFile | null>(null);
  const [isCrlvViewerOpen, setIsCrlvViewerOpen] = useState(false);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterTipo, filterMarca, filterCor, filterAno, filterMotorista, filterAgregado, filterStatus]);

  // Form modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVeiculo, setSelectedVeiculo] = useState<Veiculo | null>(null);

  // Reference tables
  const [tipos, setTipos] = useState<TipoVeiculo[]>([]);
  const [marcas, setMarcas] = useState<VeiculoMarca[]>([]);
  const [modelos, setModelos] = useState<VeiculoModelo[]>([]);

  // Form input states
  const [placa, setPlaca] = useState('');
  const [renavam, setRenavam] = useState('');
  const [crlv, setCrlv] = useState('');
  const [tipoVeiculoId, setTipoVeiculoId] = useState<number | ''>('');
  const [marcaId, setMarcaId] = useState<number | ''>('');
  const [modeloId, setModeloId] = useState<number | ''>('');
  const [anoId, setAnoId] = useState<number | ''>('');
  const [corId, setCorId] = useState<number | ''>('');
  const [isAtivo, setIsAtivo] = useState<boolean>(true);
  const [motoristaId, setMotoristaId] = useState<number | ''>('');
  const [agregadoId, setAgregadoId] = useState<number | ''>('');
  const [repasseAgregado, setRepasseAgregado] = useState<number | ''>('');

  // Additional reference tables
  const [cores, setCores] = useState<VeiculoCor[]>([]);
  const [anos, setAnos] = useState<VeiculoAno[]>([]);
  const [motoristas, setMotoristas] = useState<{ id: number; nome: string }[]>([]);
  const [agregados, setAgregados] = useState<{ id: number; nome: string }[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      setErrorMsg(null);
      const [list, tiposData, marcasData, modelosData, coresData, anosData, motoristasData, agregadosData, allPessoasRes] = await Promise.all([
        veiculoService.getAll(),
        veiculoService.getTiposVeiculo(),
        veiculoService.getMarcas(),
        veiculoService.getModelos(),
        veiculoService.getCores(),
        veiculoService.getAnos(),
        veiculoService.getMotoristas(),
        veiculoService.getAgregados(),
        veiculoService.getAllPessoas(),
      ]);

      setVeiculos(list);
      setTipos(tiposData);
      setMarcas(marcasData);
      setModelos(modelosData);
      setCores(coresData);
      setAnos(anosData);
      setMotoristas(motoristasData);
      setAgregados(agregadosData);

      if (allPessoasRes?.data) {
        const map: Record<number, string> = {};
        allPessoasRes.data.forEach((p: any) => {
          map[p.id] = p.nome;
        });
        setPessoasMap(map);
      }
    } catch (err: any) {
      console.error('Error fetching veiculos:', err);
      setErrorMsg('Erro ao carregar lista de veículos.');
    } finally {
      setLoading(false);
    }
  }

  const toggleExpandRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getMotoristaNome = (v: Veiculo) => {
    if (!v.motorista_id) return null;
    return pessoasMap[v.motorista_id] || motoristas.find((m) => m.id === v.motorista_id)?.nome || null;
  };

  const getAgregadoNome = (v: Veiculo) => {
    if (!v.agregado_id) return null;
    return pessoasMap[v.agregado_id] || agregados.find((a) => a.id === v.agregado_id)?.nome || null;
  };

  const handleOpenModal = (veiculo?: Veiculo) => {
    if (veiculo) {
      setSelectedVeiculo(veiculo);
      setPlaca(veiculo.placa || '');
      setRenavam(veiculo.renavam || '');
      setCrlv(veiculo.crlv || '');
      setTipoVeiculoId(veiculo.tipo_veiculo || '');
      setMarcaId(veiculo.veiculo_marca_id || '');
      setModeloId(veiculo.veiculo_modelo_id || '');
      setAnoId(veiculo.veiculo_ano_id || '');
      setCorId(veiculo.veiculo_cor_id || '');
      setMotoristaId(veiculo.motorista_id || '');
      setAgregadoId(veiculo.agregado_id || '');
      setRepasseAgregado(veiculo.valor_repasse_agregado || '');
      setIsAtivo(veiculo.ativo !== false);
    } else {
      setSelectedVeiculo(null);
      setPlaca('');
      setRenavam('');
      setCrlv('');
      setTipoVeiculoId('');
      setMarcaId('');
      setModeloId('');
      setAnoId('');
      setCorId('');
      setMotoristaId('');
      setAgregadoId('');
      setRepasseAgregado('');
      setIsAtivo(true);
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const placaFormatada = placa.toUpperCase().trim();
      const novoMotId = motoristaId ? Number(motoristaId) : null;
      const veiculoIdFinal = selectedVeiculo ? selectedVeiculo.id : (placaFormatada || Math.random().toString(36).substring(7));

      const payload: Veiculo = {
        id: veiculoIdFinal,
        placa: placaFormatada,
        renavam: renavam || null,
        crlv: crlv || null,
        tipo_veiculo: tipoVeiculoId ? Number(tipoVeiculoId) : null,
        veiculo_marca_id: marcaId ? Number(marcaId) : null,
        veiculo_modelo_id: modeloId ? Number(modeloId) : null,
        veiculo_ano_id: anoId ? Number(anoId) : null,
        veiculo_cor_id: corId ? Number(corId) : null,
        motorista_id: novoMotId,
        agregado_id: agregadoId ? Number(agregadoId) : null,
        valor_repasse_agregado: repasseAgregado ? Number(repasseAgregado) : null,
        ativo: isAtivo,
        data_desativacao: isAtivo ? null : new Date().toISOString(),
      };

      if (selectedVeiculo) {
        await veiculoService.update(selectedVeiculo.id, payload);

        // Se o motorista mudou em relação ao anterior, registra no histórico.
        // Inclui a remoção do condutor (novoMotId nulo), que apenas encerra o
        // período do motorista anterior.
        if ((selectedVeiculo.motorista_id ?? null) !== novoMotId) {
          const motObj = novoMotId ? motoristas.find(m => m.id === novoMotId) : null;
          await veiculoHistoricoMotoristaService.registrarTrocaMotorista({
            veiculo_id: selectedVeiculo.id,
            placa: placaFormatada,
            novo_motorista_id: novoMotId,
            novo_motorista_nome: novoMotId ? (motObj?.nome || `Motorista #${novoMotId}`) : null,
            motivo: novoMotId
              ? 'Atualização no cadastro do veículo'
              : 'Condutor removido no cadastro do veículo',
          });
        }
      } else {
        await veiculoService.create(payload);
        // Se cadastrou novo veículo com motorista já atribuído
        if (novoMotId) {
          const motObj = motoristas.find(m => m.id === novoMotId);
          await veiculoHistoricoMotoristaService.registrarTrocaMotorista({
            veiculo_id: veiculoIdFinal,
            placa: placaFormatada,
            novo_motorista_id: novoMotId,
            novo_motorista_nome: motObj?.nome || `Motorista #${novoMotId}`,
            motivo: 'Cadastro inicial do veículo',
          });
        }
      }

      setIsModalOpen(false);
      fetchData();
    } catch (err: any) {
      alert('Erro ao salvar veículo: ' + err.message);
    }
  };

  const handleDelete = async (id: string, placaStr?: string | null) => {
    if (!window.confirm(`Deseja excluir o veículo ${placaStr || id}?`)) return;
    try {
      await veiculoService.delete(id);
      fetchData();
    } catch (err: any) {
      alert('Erro ao excluir veículo: ' + err.message);
    }
  };

  const handleToggleAtivo = (v: Veiculo) => {
    setConfirmToggleVeiculo(v);
  };

  const executeToggleAtivo = async () => {
    if (!confirmToggleVeiculo) return;
    const v = confirmToggleVeiculo;
    const newAtivo = v.ativo === false ? true : false;
    const actionLabel = newAtivo ? 'ativado' : 'desativado';
    setConfirmToggleVeiculo(null);

    try {
      await veiculoService.update(v.id, {
        ativo: newAtivo,
        status_frota: newAtivo ? 'disponivel' : 'desativado',
        data_desativacao: newAtivo ? null : new Date().toISOString(),
      });
      await fetchData();
      showToast(`Veículo ${v.placa || v.id} ${actionLabel} com sucesso!`, 'success');
    } catch (err: any) {
      showToast(`Erro ao ${actionLabel} veículo: ` + err.message, 'error');
    }
  };

  const filtered = veiculos.filter((v) => {
    const motoristaNome = getMotoristaNome(v) || '';
    const agregadoNome = getAgregadoNome(v) || '';

    const matchesSearch = !searchTerm ||
      v.placa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.renavam?.includes(searchTerm) ||
      v.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      motoristaNome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agregadoNome.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTipo = !filterTipo || v.tipo_veiculo === filterTipo;
    const matchesMarca = !filterMarca || v.veiculo_marca_id === filterMarca;
    const matchesCor = !filterCor || v.veiculo_cor_id === filterCor;
    const matchesAno = !filterAno || v.veiculo_ano_id === filterAno;
    const matchesMotorista = !filterMotorista || v.motorista_id === filterMotorista;
    const matchesAgregado = !filterAgregado || v.agregado_id === filterAgregado;
    const matchesStatus =
      filterStatus === 'todos' ||
      (filterStatus === 'ativos' && v.ativo !== false) ||
      (filterStatus === 'inativos' && v.ativo === false) ||
      (filterStatus === 'em_manutencao' && v.status_frota === 'em_manutencao') ||
      (filterStatus === 'disponivel' && v.status_frota === 'disponivel') ||
      (filterStatus === 'em_uso' && v.status_frota === 'em_uso');

    return matchesSearch && matchesTipo && matchesMarca && matchesCor && matchesAno && matchesMotorista && matchesAgregado && matchesStatus;
  });

  const paginatedVeiculos = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      {/* Header and Sub-Navigation Tabs */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Truck className="h-7 w-7 text-red-600" />
              Gestão de Veículos & Frota
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Controle completo de veículos da frota própria, agregados terceirizados, manutenções, checklists e consumo KM/L.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center gap-3">
            {veiculoHistoricoMotoristaService.identificarPlacasDuplicadas(veiculos).size > 0 && (
              <ProtectedField permission="manage_vehicles">
                <button
                  type="button"
                  onClick={() => setIsConsolidacaoOpen(true)}
                  className="inline-flex items-center justify-center rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-bold text-amber-900 shadow-xs hover:bg-amber-100 transition-colors"
                  title="Unificar placas duplicadas em um único cadastro com histórico"
                >
                  <Merge className="-ml-0.5 mr-1.5 h-4 w-4 text-amber-700" />
                  Unificar Placas ({veiculoHistoricoMotoristaService.identificarPlacasDuplicadas(veiculos).size})
                </button>
              </ProtectedField>
            )}
            <ProtectedField permission="manage_vehicles">
              <button
                type="button"
                onClick={() => handleOpenModal()}
                className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors cursor-pointer"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" />
                Novo Veículo
              </button>
            </ProtectedField>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 pt-2 gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('cadastro')}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'cadastro'
                ? 'border-red-600 text-red-600 bg-red-50/50 rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <Truck className="w-4 h-4" />
            Cadastro de Veículos ({veiculos.length})
          </button>

          <button
            onClick={() => setActiveTab('gestao')}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'gestao'
                ? 'border-amber-600 text-amber-700 bg-amber-50/50 rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <Wrench className="w-4 h-4 text-amber-600" />
            Frota Própria & Manutenção ({veiculos.filter((v) => !v.agregado_id).length})
          </button>

          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'dashboard'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50 rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <LayoutDashboard className="w-4 h-4 text-blue-600" />
            Dashboard, KPIs & Relatórios
          </button>
        </div>
      </div>

      {/* CONDITIONAL TAB RENDERING */}
      {activeTab === 'dashboard' ? (
        <VeiculosDashboard
          veiculos={veiculos}
          motoristasList={motoristas}
          onOpenManutencao={(v) => setManutencaoVeiculo(v)}
          onOpenChecklist={(v) => setChecklistVeiculo(v)}
          onOpenAbastecimento={(v) => setAbastecimentoVeiculo(v)}
        />
      ) : activeTab === 'gestao' ? (
        <VeiculoGestaoFrotaTable
          veiculosPropria={veiculos.filter((v) => !v.agregado_id)}
          getTipoNome={(id) => tipos.find((t) => t.id === id)?.tipo_veiculo || ''}
          getMarcaNome={(id) => marcas.find((m) => m.id === id)?.marca || ''}
          getModeloNome={(id) => modelos.find((m) => m.id === id)?.modelo || ''}
          getCorNome={(id) => cores.find((c) => c.id === id)?.cor || ''}
          getAnoNome={(id) => anos.find((a) => a.id === id)?.ano ? String(anos.find((a) => a.id === id)?.ano) : ''}
          getMotoristaNome={(v) => {
            if (v.motorista_id && pessoasMap[v.motorista_id]) return pessoasMap[v.motorista_id];
            return '';
          }}
          onOpenManutencao={(v) => setManutencaoVeiculo(v)}
          onOpenChecklist={(v) => setChecklistVeiculo(v)}
          onOpenAbastecimento={(v) => setAbastecimentoVeiculo(v)}
          onOpenHistorico={(v) => setHistoricoVeiculo(v)}
          onOpenEdit={(v) => handleOpenModal(v)}
          onToggleAtivo={(v) => handleToggleAtivo(v)}
        />
      ) : (
        /* ABA CADASTRO DE VEÍCULOS (Geral - Frota Própria e Agregados) */
        <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
        
        {/* Banner Informativo sobre Gestão de Placas Duplicadas e Histórico */}
        {(() => {
          const dupes = veiculoHistoricoMotoristaService.identificarPlacasDuplicadas(veiculos);
          if (dupes.size === 0) return null;
          return (
            <div className="bg-amber-50/80 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap text-xs text-amber-900">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <strong className="font-bold">Histórico de Motoristas Ativo:</strong> Identificamos {dupes.size} {dupes.size === 1 ? 'placa com cadastros múltiplos' : 'placas com cadastros múltiplos'}. 
                  Agora você pode usar o botão <span className="inline-flex items-center gap-1 font-bold bg-white px-1.5 py-0.5 rounded border border-amber-300"><History className="w-3 h-3 text-slate-700" /> Histórico</span> para alternar motoristas sem duplicar o veículo e sem alterar as OSs passadas!
                </div>
              </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsConsolidacaoOpen(true)}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-xs transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer"
                  >
                    <Merge className="w-3.5 h-3.5" />
                    Unificar Placas Agora
                  </button>
                  {Array.from(dupes.keys()).slice(0, 3).map(placaDup => (
                    <button
                      key={placaDup}
                      type="button"
                      onClick={() => setSearchTerm(placaDup)}
                      className="px-2 py-1 bg-white hover:bg-amber-100 border border-amber-300 rounded font-mono font-bold text-3xs text-amber-900 transition-colors cursor-pointer"
                      title={`Filtrar placa ${placaDup}`}
                    >
                      🔍 {placaDup} ({dupes.get(placaDup)?.length})
                    </button>
                  ))}
                </div>
            </div>
          );
        })()}

        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {/* Search input */}
            <div className="relative rounded-md shadow-sm w-full sm:col-span-2 lg:col-span-2">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border-slate-300 pl-10 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 border outline-none bg-white"
                placeholder="Placa, Motorista, Agregado ou RENAVAM..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Motorista Filter */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700"
                value={filterMotorista}
                onChange={(e) => setFilterMotorista(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todos os Motoristas</option>
                {motoristas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Agregado Filter */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700"
                value={filterAgregado}
                onChange={(e) => setFilterAgregado(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todos os Agregados</option>
                {agregados.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700"
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todos os Tipos</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.tipo_veiculo}
                  </option>
                ))}
              </select>
            </div>

            {/* Marca */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700"
                value={filterMarca}
                onChange={(e) => setFilterMarca(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todas as Marcas</option>
                {marcas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.marca}
                  </option>
                ))}
              </select>
            </div>

            {/* Cor / Ano combined or separate */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700"
                value={filterAno}
                onChange={(e) => setFilterAno(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todos os Anos</option>
                {anos.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.ano}
                  </option>
                ))}
              </select>
            </div>
            {/* Status Filter */}
            <div>
              <select
                className="block w-full rounded-lg border-slate-300 focus:border-red-500 focus:ring-red-500 sm:text-sm py-2 px-3 border outline-none bg-white text-slate-700 font-medium"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <option value="todos">Status: Todos</option>
                <option value="ativos">Apenas Ativos</option>
                <option value="disponivel">Disponíveis (Pronto)</option>
                <option value="em_manutencao">Em Manutenção (Oficina)</option>
                <option value="em_uso">Em Uso (Viagem)</option>
                <option value="inativos">Apenas Inativos</option>
              </select>
            </div>
          </div>

          {/* Quick Clear Filter button when any filter is active */}
          {(searchTerm || filterTipo || filterMarca || filterCor || filterAno || filterMotorista || filterAgregado || filterStatus !== 'todos') && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setFilterTipo('');
                  setFilterMarca('');
                  setFilterCor('');
                  setFilterAno('');
                  setFilterMotorista('');
                  setFilterAgregado('');
                  setFilterStatus('todos');
                }}
                className="text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                × Limpar Filtros
              </button>
            </div>
          )}
        </div>

        {errorMsg && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 m-4 rounded">
            {errorMsg}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-10 px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <span className="sr-only">Expandir</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Motorista / Agregado / Placa
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Marca / Modelo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  RENAVAM
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Repasse Agregado
                </th>
                <th className="relative px-6 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">
                    Carregando veículos...
                  </td>
                </tr>
              ) : paginatedVeiculos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">
                    Nenhum veículo encontrado.
                  </td>
                </tr>
              ) : (
                paginatedVeiculos.map((v) => {
                  const tipoObj = tipos.find((t) => t.id === v.tipo_veiculo);
                  const marcaObj = marcas.find((m) => m.id === v.veiculo_marca_id);
                  const modeloObj = modelos.find((m) => m.id === v.veiculo_modelo_id);
                  const anoObj = anos.find((a) => a.id === v.veiculo_ano_id);
                  const corObj = cores.find((c) => c.id === v.veiculo_cor_id);

                  const motoristaNome = getMotoristaNome(v);
                  const agregadoNome = getAgregadoNome(v);
                  const isExpanded = expandedRows.has(v.id);

                  return (
                    <React.Fragment key={v.id}>
                      <tr className={`hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-slate-50/80' : ''}`}>
                        <td className="px-3 py-4 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleExpandRow(v.id)}
                            className={`p-1.5 rounded-full transition-colors ${
                              isExpanded ? 'bg-red-50 text-red-600' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                            title={isExpanded ? 'Ocultar detalhes' : 'Ver detalhes do veículo'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            {/* Motorista (above) */}
                            {motoristaNome && (
                              <div className="flex items-start gap-1.5 text-xs font-semibold text-slate-800">
                                <User className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                                <span className="whitespace-normal break-words leading-tight" title={motoristaNome}>
                                  <span className="text-slate-400 font-normal">Motorista:</span> {motoristaNome}
                                </span>
                              </div>
                            )}

                            {/* Agregado (below Motorista) */}
                            {agregadoNome && (
                              <div className="flex items-start gap-1.5 text-xs font-semibold text-slate-800">
                                <Building2 className="w-3.5 h-3.5 text-purple-600 shrink-0 mt-0.5" />
                                <span className="whitespace-normal break-words leading-tight" title={agregadoNome}>
                                  <span className="text-slate-400 font-normal">Agregado:</span> {agregadoNome}
                                </span>
                              </div>
                            )}

                            {/* Placa badge with inline arrow toggle */}
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-bold bg-slate-900 text-white tracking-wider shadow-xs">
                                {v.placa || v.id}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleExpandRow(v.id)}
                                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors cursor-pointer"
                                title={isExpanded ? 'Recolher detalhes' : 'Ver detalhes'}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-red-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                          {tipoObj?.tipo_veiculo || v.tipo_veiculo || 'N/A'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                          {marcaObj?.marca || '-'} / {modeloObj?.modelo || '-'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-mono">
                          {v.renavam || '-'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {v.valor_repasse_agregado
                            ? new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(v.valor_repasse_agregado)
                            : '-'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            {!v.agregado_id ? (
                              <>
                                <ProtectedField permission="manage_maintenance">
                                  <button
                                    onClick={() => setManutencaoVeiculo(v)}
                                    className="text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 p-1.5 rounded-md border border-amber-200 cursor-pointer"
                                    title="Gerenciar Manutenção e Status da Frota Própria"
                                  >
                                    <Wrench className="h-4 w-4" />
                                  </button>
                                </ProtectedField>
                                <ProtectedField permission="manage_checklists">
                                  <button
                                    onClick={() => setChecklistVeiculo(v)}
                                    className="text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 p-1.5 rounded-md border border-blue-200 cursor-pointer"
                                    title="Checklist Pré-Viagem"
                                  >
                                    <ClipboardCheck className="h-4 w-4" />
                                  </button>
                                </ProtectedField>
                                <ProtectedField permission="manage_abastecimento">
                                  <button
                                    onClick={() => setAbastecimentoVeiculo(v)}
                                    className="text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 p-1.5 rounded-md border border-emerald-200 cursor-pointer"
                                    title="Abastecimento & KM"
                                  >
                                    <Fuel className="h-4 w-4" />
                                  </button>
                                </ProtectedField>
                              </>
                            ) : (
                              <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 text-slate-600 rounded border border-slate-200" title="Manutenção e checklist gerenciados pelo parceiro agregado">
                                🤝 Agregado
                              </span>
                            )}
                            <button
                              onClick={() => setHistoricoVeiculo(v)}
                              className="text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-md border border-slate-300 cursor-pointer"
                              title="Histórico de Motoristas do Veículo"
                            >
                              <History className="h-4 w-4" />
                            </button>
                            <ProtectedField permission="manage_vehicles">
                              <button
                                onClick={() => handleOpenModal(v)}
                                className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 p-1.5 rounded-md cursor-pointer"
                                title="Editar veículo"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </ProtectedField>
                            <ProtectedField permission="delete_vehicles">
                              <button
                                onClick={() => handleDelete(v.id, v.placa)}
                                className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 p-1.5 rounded-md cursor-pointer"
                                title="Excluir veículo"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </ProtectedField>
                          </div>
                        </td>
                      </tr>

                      {/* Expandable Accordion Details Row */}
                      {isExpanded && (
                        <tr className="bg-slate-50/70 border-b border-slate-200">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm space-y-4">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-3 flex-wrap gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-3 py-1 rounded-md text-base font-bold bg-slate-900 text-white tracking-wider">
                                    {v.placa || v.id}
                                  </span>
                                  <span className="text-sm font-semibold text-slate-800">
                                    {tipoObj?.tipo_veiculo || 'N/A'} • {marcaObj?.marca || '-'} {modeloObj?.modelo || ''}
                                  </span>

                                  {/* Status Badges */}
                                  <span
                                    className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${
                                      v.ativo !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                    }`}
                                  >
                                    {v.ativo !== false ? '● Ativo' : '○ Inativo'}
                                  </span>

                                  {v.status_frota === 'em_manutencao' && (
                                     <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                                       <Wrench className="w-3 h-3 text-amber-600" /> Em Manutenção
                                     </span>
                                   )}

                                   {v.status_frota === 'em_uso' && (
                                     <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-900 border border-blue-300">
                                       Em Viagem / Uso
                                     </span>
                                   )}

                                   {v.status_frota === 'disponivel' && (
                                     <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                       Disponível (Pronto)
                                     </span>
                                   )}
                                 </div>

                                 <div className="flex items-center gap-2 flex-wrap">
                                   {!v.agregado_id ? (
                                     <>
                                       <button
                                         onClick={() => setManutencaoVeiculo(v)}
                                         className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 px-3 py-1.5 rounded-md transition-colors"
                                       >
                                         <Wrench className="w-3.5 h-3.5 text-amber-600" /> Manutenção & Status
                                       </button>

                                       <button
                                         onClick={() => setChecklistVeiculo(v)}
                                         className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-300 px-3 py-1.5 rounded-md transition-colors"
                                       >
                                         <ClipboardCheck className="w-3.5 h-3.5 text-blue-600" /> Checklist Pré-Viagem
                                       </button>

                                       <button
                                         onClick={() => setAbastecimentoVeiculo(v)}
                                         className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 px-3 py-1.5 rounded-md transition-colors"
                                       >
                                         <Fuel className="w-3.5 h-3.5 text-emerald-600" /> Abastecimento / KM
                                       </button>
                                     </>
                                   ) : (
                                     <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 text-slate-600 rounded border border-slate-200">
                                       Veículo Agregado (Manutenção Terceirizada)
                                     </span>
                                   )}
                                 </div>

                                 <button
                                   onClick={() => handleToggleAtivo(v)}
                                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors border ${
                                      v.ativo !== false
                                        ? 'text-amber-700 bg-slate-50 hover:bg-amber-50 border-slate-200'
                                        : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200'
                                    }`}
                                  >
                                    {v.ativo !== false ? 'Desativar' : 'Ativar'}
                                  </button>

                                  <button
                                    onClick={() => handleOpenModal(v)}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors border border-red-200"
                                  >
                                    <Edit className="w-3.5 h-3.5" /> Editar
                                  </button>
                                </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    RENAVAM
                                  </span>
                                  <span className="font-mono text-slate-800 font-semibold">{v.renavam || '-'}</span>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex flex-col justify-between">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Documento CRLV
                                  </span>
                                  {v.crlv ? (
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const rawFilename = v.crlv.split('/').pop() || v.crlv;
                                          let filename = rawFilename;
                                          try {
                                            filename = decodeURIComponent(rawFilename).replace(/\+/g, ' ');
                                          } catch {
                                            filename = rawFilename.replace(/\+/g, ' ');
                                          }
                                          const ext = filename.split('.').pop()?.toLowerCase() || 'pdf';
                                          const relPath = (v.crlv.startsWith('http') || v.crlv.startsWith('data:') || v.crlv.startsWith('file:'))
                                            ? v.crlv
                                            : v.crlv.startsWith('/api/crlv/file/')
                                            ? v.crlv
                                            : `/api/crlv/file/CRLV/${v.crlv.replace(/^\/?(CRLV|crlv)\/?/i, '')}`;
                                          setSelectedCrlvFile({
                                            id: v.crlv,
                                            folder: 'crlv',
                                            name: filename,
                                            relativePath: relPath,
                                            extension: ext,
                                          });
                                          setIsCrlvViewerOpen(true);
                                        }}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition cursor-pointer"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        Visualizar CRLV
                                      </button>
                                      <span className="text-[10px] text-slate-400 font-mono truncate max-w-[120px]" title={v.crlv}>
                                        {v.crlv.split('/').pop()}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-xs italic">Não anexado</span>
                                  )}
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Ano Fabricação
                                  </span>
                                  <span className="text-slate-800 font-semibold">{anoObj?.ano || '-'}</span>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Cor
                                  </span>
                                  <span className="text-slate-800 font-semibold">{corObj?.cor || '-'}</span>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Motorista
                                  </span>
                                  <span className="text-slate-800 font-semibold">{motoristaNome || 'Nenhum'}</span>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Agregado
                                  </span>
                                  <span className="text-slate-800 font-semibold">{agregadoNome || 'Nenhum'}</span>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 md:col-span-2">
                                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                    Valor de Repasse Agregado
                                  </span>
                                  <span className="text-slate-900 font-bold text-base">
                                    {v.valor_repasse_agregado
                                      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                                          v.valor_repasse_agregado
                                        )
                                      : '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={filtered.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(items) => {
            setItemsPerPage(items);
            setCurrentPage(1);
          }}
        />
      </div>
      )}

      {/* Modal Veículo */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              {selectedVeiculo ? 'Editar Veículo' : 'Novo Veículo'}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Placa</label>
                  <input
                    type="text"
                    required
                    maxLength={8}
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500 uppercase"
                    value={placa}
                    onChange={(e) => setPlaca(e.target.value)}
                    placeholder="ABC1D23"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Tipo de Veículo</label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={tipoVeiculoId}
                    onChange={(e) => setTipoVeiculoId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione...</option>
                    {tipos.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.tipo_veiculo || `Tipo ${t.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 text-red-600">RENAVAM *</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={renavam}
                    onChange={(e) => setRenavam(e.target.value)}
                  />
                </div>
                <div>
                  <CrlvFieldInput
                    value={crlv}
                    onChange={setCrlv}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 text-red-600">Ano *</label>
                  <select
                    required
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={anoId}
                    onChange={(e) => setAnoId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione...</option>
                    {anos.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.ano}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 text-red-600">Cor *</label>
                  <select
                    required
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={corId}
                    onChange={(e) => setCorId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione...</option>
                    {cores.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cor}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Marca</label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={marcaId}
                    onChange={(e) => {
                      setMarcaId(e.target.value ? Number(e.target.value) : '');
                      setModeloId(''); // Reset modelo when marca changes
                    }}
                  >
                    <option value="">Selecione...</option>
                    {marcas.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.marca}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Modelo</label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={modeloId}
                    onChange={(e) => setModeloId(e.target.value ? Number(e.target.value) : '')}
                    disabled={!marcaId}
                  >
                    <option value="">Selecione...</option>
                    {modelos
                      .filter((m) => m.marca === marcaId)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.modelo}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Motorista</label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={motoristaId}
                    onChange={(e) => setMotoristaId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione...</option>
                    {motoristas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Agregado</label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={agregadoId}
                    onChange={(e) => setAgregadoId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione...</option>
                    {agregados.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Valor de Repasse (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2 shadow-sm focus:border-red-500 focus:ring-red-500"
                    value={repasseAgregado}
                    onChange={(e) =>
                      setRepasseAgregado(e.target.value ? Number(e.target.value) : '')
                    }
                  />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isAtivo}
                        onChange={(e) => setIsAtivo(e.target.checked)}
                      />
                      <div className={`block w-10 h-6 rounded-full transition-colors ${isAtivo ? 'bg-red-500' : 'bg-slate-300'}`}></div>
                      <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isAtivo ? 'transform translate-x-4' : ''}`}></div>
                    </div>
                    <div className="ml-3 text-sm font-medium text-slate-700">
                      {isAtivo ? 'Ativo' : 'Inativo'}
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feature Modals */}
      <VeiculoManutencaoModal
        veiculo={manutencaoVeiculo}
        isOpen={!!manutencaoVeiculo}
        onClose={() => setManutencaoVeiculo(null)}
        onSuccess={fetchData}
      />

      <VeiculoChecklistModal
        veiculo={checklistVeiculo}
        motoristasList={motoristas}
        isOpen={!!checklistVeiculo}
        onClose={() => setChecklistVeiculo(null)}
        onSuccess={fetchData}
      />

      <VeiculoAbastecimentoModal
        veiculo={abastecimentoVeiculo}
        motoristasList={motoristas}
        isOpen={!!abastecimentoVeiculo}
        onClose={() => setAbastecimentoVeiculo(null)}
        onSuccess={fetchData}
      />

      <VeiculoHistoricoMotoristasModal
        veiculo={historicoVeiculo}
        isOpen={!!historicoVeiculo}
        onClose={() => setHistoricoVeiculo(null)}
        motoristasList={motoristas}
        onMotoristaAtualizado={async (novoMotId) => {
          // O histórico sozinho não altera o cadastro: é aqui que o novo
          // condutor passa a valer no registro do veículo.
          if (historicoVeiculo && novoMotId) {
            try {
              await veiculoService.update(historicoVeiculo.id, { motorista_id: novoMotId });
            } catch (err: any) {
              alert('Erro ao aplicar o novo motorista no cadastro do veículo: ' + err.message);
            }
          }
          fetchData();
        }}
      />

      <VeiculoConsolidacaoModal
        isOpen={isConsolidacaoOpen}
        onClose={() => setIsConsolidacaoOpen(false)}
        veiculos={veiculos}
        motoristasList={motoristas}
        marcasList={marcas}
        modelosList={modelos}
        anosList={anos}
        coresList={cores}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Modal de Confirmação de Status do Veículo */}
      {confirmToggleVeiculo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${confirmToggleVeiculo.ativo !== false ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {confirmToggleVeiculo.ativo !== false ? <ShieldAlert className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
              </div>
              <div>
                <h3 className="font-bold text-base text-slate-900">
                  {confirmToggleVeiculo.ativo !== false ? 'Desativar Veículo' : 'Ativar Veículo'}
                </h3>
                <p className="text-xs text-slate-500 font-mono">
                  Placa: <strong>{confirmToggleVeiculo.placa || confirmToggleVeiculo.id}</strong>
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed">
              {confirmToggleVeiculo.ativo !== false ? (
                <>
                  Tem certeza que deseja desativar o veículo <strong>{confirmToggleVeiculo.placa || confirmToggleVeiculo.id}</strong>?
                  <br />
                  <span className="text-xs text-rose-600 font-semibold block mt-1.5">
                    • Ele será marcado como inativo no sistema.
                    <br />
                    • Não ficará mais disponível para seleção em novas Ordens de Serviço.
                  </span>
                </>
              ) : (
                <>
                  Deseja reativar o veículo <strong>{confirmToggleVeiculo.placa || confirmToggleVeiculo.id}</strong>?
                  <br />
                  <span className="text-xs text-emerald-600 font-semibold block mt-1.5">
                    • Ele voltará a ficar disponível para seleção nas Ordens de Serviço.
                  </span>
                </>
              )}
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmToggleVeiculo(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={executeToggleAtivo}
                className={`px-4 py-2 text-xs font-bold text-white rounded-xl shadow-sm transition-colors cursor-pointer ${
                  confirmToggleVeiculo.ativo !== false
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {confirmToggleVeiculo.ativo !== false ? 'Sim, Desativar' : 'Sim, Ativar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-800 animate-in fade-in slide-in-from-bottom-5 duration-300">
          {toastMessage.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0" />
          )}
          <span className="text-xs font-medium tracking-wide">{toastMessage.text}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="ml-2 text-slate-400 hover:text-white text-xs font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* CRLV Viewer Modal */}
      <CrlvViewerModal
        isOpen={isCrlvViewerOpen}
        onClose={() => setIsCrlvViewerOpen(false)}
        crlvFile={selectedCrlvFile}
      />
    </div>
  );
}
